import React, { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import api from '../../../api/api';

export default function DirectCatalogAlbumPicker({ source, manualAlbum, onSelect, disabled }) {
  const label = source === 'POSTPOP' ? '수동 등록 앨범' : 'Apple Music 수집 앨범';
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [criteria, setCriteria] = useState({ q: '', relatedTo: undefined });
  const sequence = useRef(0);
  useEffect(
    () => () => {
      sequence.current += 1;
    },
    [],
  );
  const search = async (page = 0, filter = { q: query.trim(), relatedTo: undefined }) => {
    const seq = ++sequence.current;
    setLoading(true);
    setError('');
    setResult(null);
    setCriteria(filter);
    try {
      const response = await api.getAuth('/admin/direct-catalog/apple-albums', {
        params: { source, page, ...filter },
      });
      if (seq === sequence.current) setResult(response.data.data);
    } catch (e) {
      if (seq === sequence.current)
        setError(e.message || '앨범을 불러오지 못했습니다. 다시 조회해주세요.');
    } finally {
      if (seq === sequence.current) setLoading(false);
    }
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1">{label} 조회</Typography>
        <TextField
          label={`${label} 제목·아티스트 검색`}
          value={query}
          inputProps={{ maxLength: 100 }}
          disabled={disabled || loading}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button disabled={disabled || loading} onClick={() => search()}>
            {label} 검색 / 전체 조회
          </Button>
          {source === 'APPLE_MUSIC' && manualAlbum && (
            <Button
              disabled={disabled || loading}
              onClick={() => search(0, { q: '', relatedTo: manualAlbum.albumId })}
            >
              선택 앨범의 중복 후보 조회
            </Button>
          )}
        </Stack>
        {loading && <Typography role="status">조회 중…</Typography>}
        {error && <Alert severity="error">{error}</Alert>}
        {criteria.relatedTo && result && (
          <Alert severity="info">
            같은 제목·아티스트로 이미 수집된 후보입니다. 같은 판본으로 확정된 것은 아닙니다. 결과가
            없으면 제목·아티스트 검색도 이용하세요.
          </Alert>
        )}
        {result && !result.items.length && <Typography>조회된 앨범이 없습니다.</Typography>}
        {result?.items.map((item) => (
          <Stack key={item.albumId} direction="row" spacing={1.5} alignItems="center">
            <Avatar variant="rounded" src={item.imageUrl || undefined} alt="" />
            <Stack sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
              <Typography>
                {item.title} · {item.artistName || '아티스트 미등록'}
              </Typography>
              <Typography variant="caption">
                {item.releaseDate || '발매일 미등록'} · {item.totalTracks}곡 · 내부 ID{' '}
                {item.albumId}
                {item.hidden ? ' · 숨김' : ''}
              </Typography>
              <Typography variant="caption">
                {source === 'POSTPOP'
                  ? item.appleMusicId
                    ? `공식 연결됨: ${item.appleMusicId}`
                    : '공식 미연결'
                  : `Apple ID ${item.appleMusicId} · 별도 수집 레코드(자동 병합 불가)`}
              </Typography>
            </Stack>
            <Button
              disabled={disabled || loading}
              aria-label={`${label} ${item.title} 선택`}
              onClick={() => onSelect(item)}
            >
              선택
            </Button>
          </Stack>
        ))}
        {result && (
          <Stack direction="row" spacing={1}>
            <Button
              disabled={disabled || loading || result.page === 0}
              onClick={() => search(result.page - 1, criteria)}
            >
              이전
            </Button>
            <Typography>{result.page + 1}페이지</Typography>
            <Button
              disabled={disabled || loading || !result.hasMore}
              onClick={() => search(result.page + 1, criteria)}
            >
              다음
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
